/**
 * data/quests/rival — the Warm Rival's side-quest chain (rival-arc.md).
 *
 * Four milestone quests that thread the Badge Circuit alongside your rival: meeting them,
 * your first friendly spar, a mid-circuit spar, and a pre-League send-off. Every quest is
 * `kind:'side'`, soft-gated by badge (VISIBILITY only), auto-tracked off real play events,
 * and reuses ONLY existing objective kinds (talk-to / win-friendly-battles) — no
 * shared/quest.ts change. None expire, none fail, all ignorable (the no-pressure contract).
 *
 * Giver: `role:rival` — GameSession.resolveGiver binds it to the pinned RivalState.trainerId
 * (choosing + persisting the rival on first resolution). The talk-to objectives also target
 * `role:rival`; the GymScene cameo emits the matching `talk-to` event so meeting the rival
 * in the anteroom ticks them. Read-only data; the save stores progress only.
 */
import type { ObjectiveDef, QuestDef, QuestStage, StageDialogue } from '@/shared';

/** The abstract giver id the session binds to the pinned rival's real SimTrainer id. */
export const RIVAL_ROLE = 'role:rival';

function stage(
  id: string,
  objectives: readonly ObjectiveDef[],
  onStart: readonly string[],
  onComplete: readonly string[],
): QuestStage {
  const dialogue: StageDialogue = { onStart, onComplete };
  return { id, objectives, dialogue };
}

function rivalQuest(
  id: string,
  title: string,
  synopsis: string,
  available: QuestDef['available'],
  objectives: readonly ObjectiveDef[],
  hook: readonly string[],
  completion: readonly string[],
  reward: QuestDef['reward'],
): QuestDef {
  return {
    id,
    kind: 'side',
    title,
    giver: RIVAL_ROLE,
    synopsis,
    available,
    tone: ['warm', 'eager', 'loyal', 'never-gloats'],
    reward,
    stages: [stage('stage-1', objectives, hook, completion)],
  };
}

export const RIVAL_QUESTS: readonly QuestDef[] = [
  // 1 — Meeting them. Available from the very start; the gym-anteroom cameo (or a town
  // greeting) fires the talk-to that ticks it. Reward: a warm keepsake ribbon.
  rivalQuest(
    'side-rival-meet',
    'A Familiar Face',
    'That friendly face keeps turning up wherever you go — go say a proper hello.',
    { kind: 'always' },
    [{ kind: 'talk-to', trainerId: RIVAL_ROLE }],
    [
      "Hey — HEY! It's you! I was hoping I'd run into you. I picked my starter the same day you did, you know. Fancy walking this circuit as friends? I'll be cheering you the whole way.",
    ],
    [
      "Best hello I've had all season! We're circuit friends now — official. Whatever gym you're at, look for me in the front row.",
    ],
    { kind: 'follower-cosmetic', cosmeticId: 'rival-ribbon' },
  ),

  // 2 — Your first friendly spar. Any friendly/gym win ticks it (a rival spar counts).
  rivalQuest(
    'side-rival-first-spar',
    "First Round's on Me",
    'Your rival has been itching for a no-stakes round — trade a friendly battle whenever.',
    { kind: 'badge', ref: 'meadow-badge' },
    [{ kind: 'win-friendly-battles', count: 1 }],
    [
      "No stakes, just us — one friendly round? My partner's been dying to say hi to your team. Win or lose, first round's on me!",
    ],
    [
      "Ahh, that was GORGEOUS — I don't even mind losing to a friend. Let's do it again soon, yeah? I'm right behind you.",
    ],
    { kind: 'rare-candies', count: 3 },
  ),

  // 3 — Mid-circuit spar. A warm evolution stone as the arc deepens.
  rivalQuest(
    'side-rival-midpoint',
    'Neck and Neck',
    'Halfway up the circuit and still side by side — your rival wants another close round.',
    { kind: 'badge', ref: 'cavern-badge' },
    [{ kind: 'win-friendly-battles', count: 1 }],
    [
      "Look at us, halfway up the circuit together! One more friendly round, for the road? My team's grown right alongside yours — let's see.",
    ],
    [
      "Neck and neck the whole way and I wouldn't have it any other way. Here — this stone's been burning a hole in my bag. It's yours.",
    ],
    { kind: 'evolution-stone', itemId: 'fire-stone' },
  ),

  // 4 — Pre-League send-off (all 8 badges). A warm town keepsake before the summit.
  rivalQuest(
    'side-rival-send-off',
    'See You at the Summit',
    'Eight badges each way — your rival wants a proper send-off before the League.',
    { kind: 'badge', ref: 'ashland-badge' },
    [{ kind: 'talk-to', trainerId: RIVAL_ROLE }],
    [
      "Eight badges. EIGHT! Look how far you've come — I got a front-row seat to every one of them. Go on to the League and give 'em the best of you. I'll be cheering loudest.",
    ],
    [
      "Whatever happens up there, you already walked the whole circuit kind and true, with me right beside you. Couldn't ask for a better friend to chase. See you at the summit!",
    ],
    { kind: 'town-prop', propId: 'rival-send-off-banner' },
  ),
];
